/**
 * The Atlas's floating panels: draggable and remembering where they were put,
 * with a way back to the default layout once anything has moved.
 */

import { makeDraggable, resetAllHudPositions, hasSavedHudLayout } from '../../hudDrag.ts';
import { wirePaneMinimize } from '../../hudMinimize.ts';
import type { PlotControlsHandle } from './plotControls.ts';
import type { TelemetryPanelHandle } from './telemetry.ts';

export interface PanelLayoutDeps {
  readonly overlay: HTMLElement;
  readonly plotControls: PlotControlsHandle;
  readonly elSampler: HTMLElement;
  readonly telemetryPanel: TelemetryPanelHandle;
  readonly root: HTMLElement;
  own(fn: () => void): void;
}

export interface PanelLayoutHandle {
  /**
   * The Layers list opened or closed. Auto-minimizes the GROUND panel where
   * Layers leaves it under `SAMPLER_AUTO_MIN_FLOOR` px of body, and restores
   * it on close - but only where this is what minimized it, never a collapse
   * the player chose with the panel's own button.
   */
  layersOpenChanged(open: boolean): void;
}

export function createPanelLayout(deps: PanelLayoutDeps): PanelLayoutHandle {
  const { overlay, elSampler, root, own } = deps;
  const { elPlotCtl } = deps.plotControls;
  const { telemEl } = deps.telemetryPanel;

  // ── panel layout ────────────────────────────────────────────────────────
  // Every floating panel is draggable and remembers where it was put, the same
  // way the naval sim's HUD widgets do — grab the strip at the top edge, or
  // right-click it to send that one panel home.
  //
  // These were previously wired in three different places, two of them inside
  // a slider's input handler, and the inspector — the widest panel on screen —
  // was not wired at all.
  const PANELS: Array<[HTMLElement | null, string]> = [
    [elPlotCtl, 'atlas-plotctl'],
    [elSampler, 'atlas-sampler'],
    [telemEl, 'atlas-telemetry'],
    [overlay.querySelector('#atlas-inspector'), 'atlas-inspector'],
  ];
  // The pane, not the window, is the box these live in: while the Solar System
  // view is open the Atlas is drawn inside the menu modal, and a panel pinned
  // to the window is carried straight out of it.
  const paneBox = (): DOMRect | null => root.getBoundingClientRect();
  for (const [el, id] of PANELS) if (el) makeDraggable(el, id, paneBox);

  // Minimize, from the same header family the sim's panels carry.
  const pcMinBtn = overlay.querySelector('#pc-minimize') as HTMLButtonElement | null;
  if (pcMinBtn) own(wirePaneMinimize(elPlotCtl, pcMinBtn).dispose);
  const gpMinBtn = overlay.querySelector('#gp-minimize') as HTMLButtonElement | null;
  const gpMinimize = gpMinBtn ? wirePaneMinimize(elSampler, gpMinBtn) : null;
  if (gpMinimize) own(gpMinimize.dispose);

  // Below this many px of body the GROUND panel is down to a couple of rows -
  // the Layers list open at 1366x768 pushes it there, so it minimizes itself
  // rather than sit half-cut. `elSampler` is `#atlas-sampler` itself; the
  // rows live in its `.atlas-sampler-body` child.
  const SAMPLER_AUTO_MIN_FLOOR = 120;
  let layersOpen = false;
  function checkSamplerAutoMin(): void {
    if (!gpMinimize) return;
    const body = layersOpen ? elSampler.querySelector<HTMLElement>('.atlas-sampler-body') : null;
    let short = false;
    if (body !== null && elSampler.classList.contains('show')) {
      // An already-minimized panel hides its own body (the shared
      // `[data-widget].hud-minimized` rule), which would read 0 here forever
      // and never let a recovered window restore it - measure with that
      // collapse lifted rather than through it.
      const hidden = elSampler.classList.contains('hud-minimized');
      const prevDisplay = body.style.display;
      if (hidden) body.style.setProperty('display', 'block', 'important');
      try {
        short = body.clientHeight < SAMPLER_AUTO_MIN_FLOOR;
      } finally {
        if (hidden) body.style.display = prevDisplay;
      }
    }
    gpMinimize.setAuto(short);
  }
  function layersOpenChanged(open: boolean): void {
    layersOpen = open;
    checkSamplerAutoMin();
  }
  // A window resize alone, with Layers already open, shrinks `#atlas-sampler`
  // through its own `:has(.atlas-vl.open)` CSS rule without ever touching the
  // Layers button - re-run the same check so GROUND still auto-minimizes and
  // un-minimizes as the window crosses the floor. Throttled to one pending
  // frame the way hudMinimize.ts's queueFit throttles its own resize refit.
  let resizeRaf: number | null = null;
  const onWindowResize = (): void => {
    if (!layersOpen || resizeRaf !== null) return;
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = null;
      checkSamplerAutoMin();
    });
  };
  window.addEventListener('resize', onWindowResize);
  own(() => window.removeEventListener('resize', onWindowResize));
  own(() => { if (resizeRaf !== null) cancelAnimationFrame(resizeRaf); });

  // A way back. Panels persist across sessions, so a layout dragged somewhere
  // unfortunate would otherwise be permanent.
  //
  // It only exists once there is something to undo — an escape hatch on screen
  // before anything has been moved is just another thing in front of Mars.
  const resetBtn = document.createElement('button');
  resetBtn.className = 'atlas-resetpanels';
  resetBtn.textContent = 'RESET PANELS';
  resetBtn.title = 'Return every panel to its default position';
  resetBtn.style.display = 'none';
  resetBtn.addEventListener('click', () => {
    resetAllHudPositions();
    resetBtn.style.display = 'none';
  });
  // The drag that creates something to undo happens with the pointer already
  // over the Atlas, so a mouseenter alone hid the way back until the player
  // left the pane and came back. Read after the drop, which is where the
  // layout is written down.
  const syncResetBtn = (): void => {
    const want = hasSavedHudLayout() ? '' : 'none';
    if (resetBtn.style.display !== want) resetBtn.style.display = want;
  };
  const onDrop = (): void => { requestAnimationFrame(syncResetBtn); };
  window.addEventListener('mouseup', onDrop);
  own(() => window.removeEventListener('mouseup', onDrop));
  overlay.addEventListener('mouseenter', () => {
    resetBtn.style.display = hasSavedHudLayout() ? '' : 'none';
  });
  overlay.appendChild(resetBtn);

  return { layersOpenChanged };
}
